import { createStore } from "@escapio/shared/data-store/DataStore";
import {
	fetchPrices,
	fetchPricesForCurrentTravelData,
} from "@escapio/portal/api/hotel-prices";
import { data } from "@escapio/shared/state/payload";
import { trackViewContent } from "@escapio/portal/tracker/Facebook";
import { trackNoPrices, trackAlternativeShown } from "./tracker";

export * from "@escapio/portal/state/HotelPriceLoadingStates";
import {
	STATE_AVAILABLE,
	STATE_DIRECT_LINK_ONLY,
	STATE_LOADING,
	STATE_UNAVAILABLE,
	STATE_ALTERNATIVES,
	STATE_WITHOUT_DATES,
} from "@escapio/portal/state/HotelPriceLoadingStates";
import { format, parse } from "./FilterToHashMapper";
import { formatRequestParameter, travelData } from "../../state/travelData";
import * as Board from "@escapio/portal/components/board";
import { replaceState } from "@escapio/shared/state/urlHash";
import {
	getUnavailableState,
	hasDirectLink,
} from "@escapio/portal/state/DirectLink";

const hotel = data.hotel;
const HAS_DIRECT_LINK = hasDirectLink(hotel);

const FILTER_BOARD = "board";
const FILTER_CANCELATION = "cancelation";

const DEFAULT_FILTERS = Object.freeze({
	[FILTER_BOARD]: Board.NONE,
	[FILTER_CANCELATION]: false,
});

const HAS_NO_PRICE_STATES = [
	STATE_DIRECT_LINK_ONLY,
	STATE_UNAVAILABLE,
	STATE_ALTERNATIVES,
];

export const ACTION_PARSE_HASH = (state, payload, dispatch) => {
	const { selectedStagePicture, ...filters } = parse(payload);
	const newState =
		selectedStagePicture !== undefined
			? { ...state, selectedStagePicture }
			: state;
	return ACTION_SET_FILTER(newState, filters, dispatch);
};

export const ACTION_REQUEST_PRICES = (state, _, dispatch) => {
	if (hotel.disabled_prices) {
		return { ...state, prices: null, status: getUnavailableState(hotel) };
	}

	if (!travelData.hasDates) {
		return { ...state, prices: null, status: STATE_WITHOUT_DATES };
	}

	const filters = Object.entries(state.filters)
		.filter(([, enabled]) => enabled)
		.reduce((acc, [name, value]) => ({ ...acc, [name]: value }), {});

	fetchPricesForCurrentTravelData([hotel.id], filters).then(
		response => {
			if (
				response === null ||
				!response.hasOwnProperty(hotel.id) ||
				!response[hotel.id].offers ||
				response[hotel.id].offers.length === 0
			) {
				dispatch(ACTION_REQUEST_ALTERNATIVES, filters);
				return;
			}

			dispatch(ACTION_SET_PRICES, response[hotel.id]);
		},
		() => {
			dispatch(ACTION_REQUEST_ALTERNATIVES, filters);
		},
	);

	return {
		...state,
		prices: null,
		status: STATE_LOADING,
	};
};

const ACTION_REQUEST_ALTERNATIVES = (state, filters, dispatch) => {
	Promise.all(
		[1, -1, 7, 14].map(shift => {
			const arrival = new Date(travelData.arrival.valueOf());
			const departure = new Date(travelData.departure.valueOf());
			arrival.setDate(arrival.getDate() + shift);
			departure.setDate(departure.getDate() + shift);
			return fetchPrices(
				[hotel.id],
				formatRequestParameter(
					arrival,
					departure,
					travelData.rooms,
					travelData.currency,
				),
				filters,
			).then(
				response => {
					if (
						response === null ||
						!response.hasOwnProperty(hotel.id) ||
						!response[hotel.id].offers ||
						response[hotel.id].offers.length === 0
					) {
						return;
					}

					dispatch(ACTION_ADD_ALTERNATIVE, {
						arrival,
						departure,
						bestPrice: response[hotel.id].offers[0]["total_rate"],
					});
				},
				() => {
					// Ignore.
				},
			);
		}),
	).finally(() => {
		dispatch(ACTION_FINISH_LOADING);
	});

	return {
		...state,
		status: STATE_LOADING,
		alternatives: [],
	};
};

const ACTION_SET_PRICES = (state, prices, dispatch) =>
	ACTION_SET_STATUS({ ...state, prices }, STATE_AVAILABLE, dispatch);

const ACTION_ADD_ALTERNATIVE = (state, alternative) => {
	trackAlternativeShown(hotel, alternative.arrival, travelData, state.filters);
	const byDate = (a, b) => (a.arrival < b.arrival ? -1 : 1);
	return {
		...state,
		alternatives: [...state.alternatives, alternative].sort(byDate),
	};
};

const ACTION_FINISH_LOADING = state => {
	if (state.alternatives && state.alternatives.length > 0) {
		return ACTION_SET_STATUS(state, STATE_ALTERNATIVES);
	}

	if (HAS_DIRECT_LINK) {
		return ACTION_SET_STATUS(state, STATE_DIRECT_LINK_ONLY);
	}

	return ACTION_SET_STATUS(state, STATE_UNAVAILABLE);
};

const getNoPriceAction = status => {
	switch (status) {
		case STATE_UNAVAILABLE:
			return "Profil leer";
		case STATE_DIRECT_LINK_ONLY:
			return "Profil nur EDL";
		case STATE_ALTERNATIVES:
			return HAS_DIRECT_LINK
				? "Profil EDL & Alternativen"
				: "Profil nur Alternativen";
	}
};

const ACTION_SET_STATUS = (state, status) => {
	if (status === STATE_AVAILABLE) {
		trackViewContent(hotel, getBestOfferRate(state.prices));
	} else {
		trackViewContent(hotel);
	}

	if (HAS_NO_PRICE_STATES.includes(status)) {
		trackNoPrices(getNoPriceAction(status), hotel, state.filters);
	}

	return { ...state, status };
};

const getBestOfferRate = prices => {
	const bestOffer = prices.offers.find(offer => offer.total_rate);
	if (bestOffer) {
		return bestOffer.total_rate;
	}
};

export const ACTION_SET_FILTER = (state, filters, dispatch) => {
	const newFilters = { ...state.filters, ...filters };
	const hash = format(newFilters);
	if (hash !== location.hash) {
		replaceState(hash);
	}

	return ACTION_REQUEST_PRICES(
		{ ...state, filters: newFilters },
		null,
		dispatch,
	);
};

export const store = createStore(
	{
		status: STATE_LOADING,
		prices: null,
		filters: { ...DEFAULT_FILTERS },
		alternatives: null,
		selectedStagePicture: 0,
	},
	{
		boardFilterAvailable:
			({ filters, status }) =>
			value =>
				status === STATE_AVAILABLE ||
				(HAS_NO_PRICE_STATES.includes(status) &&
					Board.isAtLeast(value, filters.board)),
		filterAvailable:
			({ filters, status }) =>
			filter =>
				status === STATE_AVAILABLE ||
				(HAS_NO_PRICE_STATES.includes(status) && filters[filter]),
	},
);
